import React, { useEffect, useRef, useState } from "react";
import { MapContainer, CircleMarker, useMapEvents } from "react-leaflet";
import { X, PushPin, Camera } from "@phosphor-icons/react";
import MapBaseLayers from "./map/MapBaseLayers";
import MapSearchControl from "./map/MapSearchControl";
import { INFO_PIN_COLOR, PIN_TYPES, pinType } from "./map/InfoPinsLayer";
import { createInfoPin, updateInfoPin, uploadFieldImage, uploadVideoChunked, fileUrl } from "../lib/api";

function ClickPicker({ onPick }) {
  useMapEvents({
    click(e) {
      onPick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

export default function InfoPinFormModal({ open, onClose, onSaved, center, pin = null, initialPoint = null, preset = null }) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [expiresDays, setExpiresDays] = useState(0);
  const [point, setPoint] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [icon, setIcon] = useState("info");
  const [videoId, setVideoId] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoProgress, setVideoProgress] = useState(null);
  const fileRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    if (open) {
      const isDebrisPreset = !pin && preset === "debris";
      setTitle(pin?.title || (isDebrisPreset ? "Debris on road" : ""));
      setNote(pin?.note || "");
      setLinkUrl(pin?.link_url || "");
      setExpiresDays(0);
      setPoint(pin ? [pin.latitude, pin.longitude] : (initialPoint || null));
      setPhotos((pin?.image_ids || []).map((id) => ({ id, url: fileUrl(id) })));
      setVideoId(pin?.video_id || null);
      setVideoUrl(pin?.video_url || "");
      setVideoProgress(null);
      setError(null);
      setIcon(pin?.icon || (isDebrisPreset ? "debris" : "info"));
    }
  }, [open, pin, initialPoint, preset]);

  if (!open) return null;
  const editing = Boolean(pin);

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 2 - photos.length);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const f of files) {
        const res = await uploadFieldImage(f);
        setPhotos((prev) => (prev.length < 2 ? [...prev, { id: res.id, url: fileUrl(res.id) }] : prev));
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Photo upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const addVideo = async (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 100 * 1024 * 1024) { setError("Video too large (max 100 MB)."); return; }
    setError(null);
    setVideoProgress(0);
    try {
      const res = await uploadVideoChunked(f, setVideoProgress);
      setVideoId(res.id);
    } catch (err) {
      setError(err.response?.data?.detail || "Video upload failed. Try again.");
    } finally {
      setVideoProgress(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!point) { setError("Click the map to place the pin."); return; }
    if (!title.trim()) { setError("Title is required."); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: title.trim(),
        note: note.trim() || null,
        link_url: linkUrl.trim() || null,
        latitude: point[0],
        longitude: point[1],
        expires_days: expiresDays > 0 ? expiresDays : null,
        image_ids: photos.map((p) => p.id),
        icon,
        video_id: videoId || "",
        video_url: videoUrl.trim(),
      };
      const saved = editing
        ? await updateInfoPin(pin.id, payload)
        : await createInfoPin(payload);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save pin. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/70" data-testid="info-pin-form-modal">
      <div className="w-full max-w-2xl bg-tac-bg border border-tac-border rounded-sm max-h-[92vh] overflow-y-auto tac-scroll">
        <div className="flex items-center justify-between px-4 py-3 border-b border-tac-border">
          <div className="flex items-center gap-2">
            <PushPin size={16} weight="fill" style={{ color: INFO_PIN_COLOR }} />
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-tac-text">
              {editing ? "Edit Info Pin" : "Add Info Pin"}
            </span>
          </div>
          <button data-testid="info-pin-form-close" onClick={onClose} className="text-tac-dim hover:text-tac-text">
            <X size={16} weight="bold" />
          </button>
        </div>

        <form onSubmit={submit} className="p-4 space-y-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
              Click the map or search an address to place the pin
            </div>
            <div className="h-64 border rounded-sm overflow-hidden" style={{ borderColor: INFO_PIN_COLOR }}>
              <MapContainer
                center={point || center || [48.4, -119.5]}
                zoom={9}
                className="h-full w-full"
                style={{ background: "#0F1311" }}
              >
                <MapBaseLayers />
                <MapSearchControl
                  startOpen
                  showMarker={false}
                  zoomTo={13}
                  onSelect={(r) => setPoint([r.lat, r.lon])}
                />
                <ClickPicker onPick={setPoint} />
                {point && (
                  <CircleMarker
                    center={point}
                    radius={8}
                    pathOptions={{ color: "#0A0D0C", weight: 2, fillColor: pinType(icon).color, fillOpacity: 1 }}
                  />
                )}
              </MapContainer>
            </div>
          </div>

          <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
                Pin icon
              </div>
            {!editing && (
              <button
                type="button"
                data-testid="pin-preset-debris"
                onClick={() => { setIcon("debris"); if (!title.trim()) setTitle("Debris on road"); }}
                className="mb-2 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-[#8B5E3C]/60 bg-[#8B5E3C]/15 text-[#C89B7B] hover:border-[#8B5E3C] transition-colors"
              >
                🪨 Quick preset: Debris on road
              </button>
            )}
            <div className="flex flex-wrap gap-1.5">
              {PIN_TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`info-pin-icon-${t.key}`}
                  onClick={() => setIcon(t.key)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                    icon === t.key
                      ? "text-white"
                      : "bg-tac-surface border-tac-border text-tac-dim hover:border-tac-borderBright"
                  }`}
                  style={icon === t.key ? { background: t.color, borderColor: t.color } : undefined}
                >
                  <span
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px]"
                    style={{ background: icon === t.key ? "rgba(255,255,255,0.9)" : t.color }}
                  >
                    {t.glyph || ""}
                  </span>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
              Title *
            </div>
            <input
              data-testid="info-pin-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Shelter open at Omak High School"
              className="w-full bg-tac-surface border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-[15px] text-tac-text placeholder:text-tac-faint outline-none"
            />
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
              Note (optional)
            </div>
            <textarea
              data-testid="info-pin-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Short details visitors should know…"
              className="w-full bg-tac-surface border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-[15px] text-tac-text placeholder:text-tac-faint outline-none resize-y"
            />
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
              Link (optional — Facebook post, article, etc.)
            </div>
            <input
              data-testid="info-pin-link-input"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://facebook.com/OkanoganCountyEM/posts/…"
              className="w-full bg-tac-surface border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-[15px] text-tac-text placeholder:text-tac-faint outline-none"
            />
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
              Photos (optional — up to 2)
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              data-testid="info-pin-photo-input"
              onChange={addPhotos}
            />
            <div className="flex items-center gap-2 flex-wrap">
              {photos.map((p) => (
                <div key={p.id} className="relative">
                  <img
                    src={p.url}
                    alt="Pin"
                    className="w-20 h-20 object-cover rounded-sm border border-tac-border"
                    data-testid={`info-pin-photo-thumb-${p.id}`}
                  />
                  <button
                    type="button"
                    data-testid={`info-pin-photo-remove-${p.id}`}
                    onClick={() => setPhotos((prev) => prev.filter((x) => x.id !== p.id))}
                    className="absolute -top-1.5 -right-1.5 bg-tac-danger text-white rounded-full p-0.5 hover:scale-110 transition-transform"
                  >
                    <X size={10} weight="bold" />
                  </button>
                </div>
              ))}
              {photos.length < 2 && (
                <button
                  type="button"
                  data-testid="info-pin-add-photo-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-20 h-20 flex flex-col items-center justify-center gap-1 border border-dashed border-tac-border rounded-sm text-tac-dim hover:border-tac-borderBright hover:text-tac-text transition-colors disabled:opacity-50"
                >
                  <Camera size={18} weight="bold" />
                  <span className="text-[9px] font-bold uppercase tracking-wider">
                    {uploading ? "Uploading…" : "Add photo"}
                  </span>
                </button>
              )}
            </div>
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
              Video (optional — upload a clip or paste a link)
            </div>
            <input
              ref={videoRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
              className="hidden"
              data-testid="info-pin-video-input"
              onChange={addVideo}
            />
            <div className="flex items-center gap-2 flex-wrap mb-2">
              {videoId ? (
                <div className="flex items-center gap-2 px-2.5 py-1.5 bg-tac-surface border border-tac-border rounded-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-tac-text">
                    ▶ Video attached
                  </span>
                  <button
                    type="button"
                    data-testid="info-pin-video-remove"
                    onClick={() => setVideoId(null)}
                    className="text-tac-danger hover:scale-110 transition-transform"
                  >
                    <X size={12} weight="bold" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="info-pin-add-video-btn"
                  onClick={() => videoRef.current?.click()}
                  disabled={videoProgress != null}
                  className="px-2.5 py-1.5 flex items-center gap-1.5 border border-dashed border-tac-border rounded-sm text-tac-dim hover:border-tac-borderBright hover:text-tac-text transition-colors disabled:opacity-60 text-[10px] font-bold uppercase tracking-wider"
                >
                  <Camera size={14} weight="bold" />
                  {videoProgress != null ? `Uploading… ${videoProgress}%` : "Upload video (max 100 MB)"}
                </button>
              )}
            </div>
            <input
              data-testid="info-pin-video-url-input"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="…or paste a YouTube / Facebook / video link"
              className="w-full bg-tac-surface border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-[15px] text-tac-text placeholder:text-tac-faint outline-none"
            />
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-tac-faint mb-1.5">
              Auto-remove after {expiresDays > 0 ? `${expiresDays} day${expiresDays > 1 ? "s" : ""}` : "— never"}
            </div>
            <input
              data-testid="info-pin-expiry-slider"
              type="range"
              min={0}
              max={30}
              step={1}
              value={expiresDays}
              onChange={(e) => setExpiresDays(Number(e.target.value))}
              className="w-full accent-tac-primary"
            />
          </div>

          {error && (
            <div data-testid="info-pin-form-error" className="text-[13px] text-tac-danger">{error}</div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              data-testid="info-pin-form-submit"
              type="submit"
              disabled={saving || uploading || videoProgress != null}
              className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-white rounded-sm disabled:opacity-50 transition-colors"
              style={{ background: INFO_PIN_COLOR }}
            >
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Pin"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
